import React, { forwardRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import { MeshLineGeometry as MeshLine, MeshLineMaterial } from "meshline";
import {
  generateArc,
  createArcMesh,
  isValidDualAngleLayout,
} from "../3d/utils/layoutUtils";
import {
  addTextLabel,
  makeCircleMarker,
} from "../3d/utils/threeUtils";
import {
  computeFingerLayout,
  computeTwoHandedFingerLayout,
  isValidSpanMeasurements,
} from "../3d/utils/fingerLayoutUtils";
import { apiConfig } from "../api/api";
import { useSafeTexture } from "../../hooks/useSafeTexture";

const ballRadius = 4.294;
const arcSegments = 256;
const arcOffset = 0.005;
const arcDistancePinToMB = 6.75;
const lineWidth = 0.05;

export const BowlingLayoutDualAngle = forwardRef((props, ref) => {
  const {
    pinToPap,
    pinToPapAngle,
    valAngleDeg,
    playerPapOffset,
    spanMeasurements,
    cameraPosition,
    ballId,
    access_token,
    onTextureStatusChange,
    onLayoutError,
  } = props;

  // DEBUG: Log all props
  console.log("[DualAngle] Props:", {
    pinToPap,
    pinToPapAngle,
    valAngleDeg,
    playerPapOffset,
    spanMeasurements,
    ballId,
    hasAccessToken: !!access_token,
  });

  const handednessFactor = spanMeasurements?.isLeftHanded ? -1 : 1;
  const imageTextureUrl =
    ballId && access_token
      ? `${apiConfig.apiServer}ballDrilled/${ballId}/texture?access_token=${access_token}`
      : null;

  const { texture, isLoading } = useSafeTexture(
    imageTextureUrl,
    onTextureStatusChange,
  );

  const layoutIsValid = useMemo(
    () =>
      isValidDualAngleLayout({
        pinToPapDistance: pinToPap,
        pinAngle: pinToPapAngle,
        valAngle: valAngleDeg,
      }),
    [pinToPap, pinToPapAngle, valAngleDeg],
  );

  // Verificar si playerPapOffset tiene valores numéricos válidos
  const hasValidPapOffset = useMemo(() => {
    const v = playerPapOffset?.vertical;
    const h = playerPapOffset?.horizontal;
    return (
      typeof v === "number" &&
      !isNaN(v) &&
      v > 0 &&
      typeof h === "number" &&
      !isNaN(h) &&
      h > 0
    );
  }, [playerPapOffset]);

  // Notificar error de layout al padre
  useEffect(() => {
    const hasError = !layoutIsValid || !hasValidPapOffset;
    console.log("[DualAngle] Layout validation:", {
      layoutIsValid,
      hasValidPapOffset,
      hasError,
    });
    onLayoutError?.(hasError);
  }, [layoutIsValid, hasValidPapOffset, onLayoutError]);

  console.log("[DualAngle] Texture state:", {
    isLoading,
    hasTexture: !!texture,
    imageTextureUrl,
  });

  const ballMaterial = new THREE.MeshStandardMaterial({
    map: texture,
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0.2,
  });

  const ball = new THREE.Mesh(
    new THREE.SphereGeometry(ballRadius, 256, 256),
    ballMaterial,
  );

  const pin = new THREE.Vector3(0, ballRadius, 0);
  makeCircleMarker(pin, 0xff0000, ball);

  const thetaMB = arcDistancePinToMB / ballRadius;
  const axis = new THREE.Vector3(1, 0, 0);
  const massBias = pin.clone().applyAxisAngle(axis, handednessFactor * thetaMB);
  const cg = pin.clone().applyAxisAngle(axis, (handednessFactor * thetaMB) / 2);

  addTextLabel({
    text: "CG",
    position: cg,
    normal: cg.clone().normalize(),
    ball,
  });
  addTextLabel({
    text: "PSA",
    position: massBias,
    normal: massBias.clone().normalize(),
    ball,
  });

  const arcPoints = generateArc({
    center: cg,
    axis,
    radius: ballRadius,
    angleSweep: 8.75 / ballRadius,
    segments: arcSegments,
    handednessFactor,
    arcOffset,
  });
  ball.add(createArcMesh(arcPoints, 0xffffff, lineWidth));

  console.log(
    "[DualAngle] Ball children after basic markers:",
    ball.children.length,
  );

  // Si el layout no es válido o playerPapOffset no tiene valores válidos,
  // mostrar solo la bola básica con Pin, CG, PSA
  if (!layoutIsValid || !hasValidPapOffset) {
    console.log(
      "[DualAngle] Returning basic ball - layoutIsValid:",
      layoutIsValid,
      "hasValidPapOffset:",
      hasValidPapOffset,
    );
    return (
      <group ref={ref}>
        <primitive object={ball} />
      </group>
    );
  }

  // Layout completo
  const angleRad = THREE.MathUtils.degToRad(pinToPapAngle);
  const firstAxis = new THREE.Vector3(1, 0, 0);
  const arcAxis = firstAxis
    .clone()
    .applyAxisAngle(pin.clone().normalize(), handednessFactor * angleRad);
  const arc2Center = pin
    .clone()
    .applyAxisAngle(arcAxis, (handednessFactor * pinToPap) / 2 / ballRadius);
  const extendedArcLength = pinToPap + 2;
  const arcPoints2 = generateArc({
    center: arc2Center,
    axis: arcAxis,
    radius: ballRadius,
    angleSweep: extendedArcLength / ballRadius,
    handednessFactor,
    arcOffset,
  });
  ball.add(createArcMesh(arcPoints2, 0xffffff, lineWidth));

  const pap = pin
    .clone()
    .applyAxisAngle(arcAxis, (handednessFactor * pinToPap) / ballRadius)
    .normalize()
    .multiplyScalar(ballRadius);
  addTextLabel({
    text: "PAP",
    position: pap,
    normal: pap.clone().normalize(),
    ball,
  });

  const valAngleRad = THREE.MathUtils.degToRad(valAngleDeg);
  const valAxis = arcAxis
    .clone()
    .applyAxisAngle(pap.clone().normalize(), handednessFactor * -valAngleRad)
    .normalize();

  const arcPoints3 = [];
  const thetaArc3 = 6 / ballRadius;
  const forwardRatio = playerPapOffset.vertical / 6;
  const backwardRatio = 1 - forwardRatio;

  for (let i = 0; i <= arcSegments; i++) {
    const t = i / arcSegments;
    const angle = thetaArc3 * (t - backwardRatio);
    const point = pap
      .clone()
      .applyAxisAngle(valAxis, handednessFactor * angle)
      .normalize()
      .multiplyScalar(ballRadius + arcOffset);
    arcPoints3.push(point);
  }
  ball.add(createArcMesh(arcPoints3, 0xffffff, lineWidth));

  const playerPAP = pap
    .clone()
    .applyAxisAngle(
      valAxis,
      (handednessFactor * playerPapOffset.vertical) / ballRadius,
    );

  const perpAxis = new THREE.Vector3()
    .crossVectors(valAxis, playerPAP.clone().normalize())
    .normalize()
    .multiplyScalar(handednessFactor);

  const thetaArc = (playerPapOffset.horizontal + 2) / ballRadius;
  const markerRatio =
    playerPapOffset.horizontal / (playerPapOffset.horizontal + 2);

  const arcPoints4 = [];
  let centerGrip = null;
  for (let i = 0; i <= arcSegments; i++) {
    const t = i / arcSegments;
    const angle = handednessFactor * thetaArc * t;
    const point = playerPAP
      .clone()
      .applyAxisAngle(perpAxis, angle)
      .normalize()
      .multiplyScalar(ballRadius + arcOffset);

    if (!centerGrip && t >= markerRatio) {
      centerGrip = point.clone();
    }

    arcPoints4.push(point);
  }
  ball.add(createArcMesh(arcPoints4, 0xffffff, lineWidth));

  let gripLineDir = null;
  if (centerGrip) {
    const thetaArc = 3 / ballRadius;
    const arcPoints5 = [];

    const arcCenterDir = playerPAP.clone().normalize();
    let basePerpAxis = new THREE.Vector3()
      .crossVectors(arcCenterDir, centerGrip.clone().normalize())
      .normalize();

    if (perpAxis.lengthSq() > 1e-8 && basePerpAxis.dot(perpAxis) < 0) {
      basePerpAxis = basePerpAxis.negate();
    }

    const rotatedAxis = basePerpAxis
      .clone()
      .applyAxisAngle(centerGrip.clone().normalize(), Math.PI / 2);
    const finalPerp = rotatedAxis.normalize();

    gripLineDir = new THREE.Vector3()
      .crossVectors(finalPerp, centerGrip.clone().normalize())
      .normalize();

    for (let i = 0; i <= arcSegments; i++) {
      const t = i / arcSegments;
      const angle = handednessFactor * thetaArc * (t - 0.5);
      const point = centerGrip
        .clone()
        .applyAxisAngle(finalPerp, angle)
        .normalize()
        .multiplyScalar(ballRadius + arcOffset);
      arcPoints5.push(point);
    }

    const gripCenterLine = new MeshLine();
    gripCenterLine.setPoints(arcPoints5);
    const material3 = new MeshLineMaterial({
      color: new THREE.Color(0xffffff),
      lineWidth: lineWidth,
    });

    const gripCenter = new THREE.Mesh(gripCenterLine, material3);
    ball.add(gripCenter);
  }

  // Solo calcular fingers si tenemos specsheet válida
  const hasValidSpecsheet = isValidSpanMeasurements(spanMeasurements);

  if (centerGrip && hasValidSpecsheet) {
    let thumbPointLocal = null;
    if (spanMeasurements?.is2Hander) {
      computeTwoHandedFingerLayout({
        ball,
        centerPoint: centerGrip,
        targetPoint: playerPAP,
        spanMeasurements,
        ballRadius,
        arcOffset,
      });
    } else {
      const { thumb } = computeFingerLayout({
        ball,
        centerPoint: centerGrip,
        spanMeasurements,
        ballRadius,
        arcOffset,
        verticalAxis: valAxis,
        referenceAxis: perpAxis,
      });
      thumbPointLocal = thumb.clone();
    }

    // Paso 1: dirección inicial que apunta desde centro hacia centerGrip
    const markerPointLocal = centerGrip.clone();
    const markerPointWorld = markerPointLocal
      .clone()
      .applyMatrix4(ball.matrixWorld);

    const cameraTargetPosition = new THREE.Vector3().fromArray(cameraPosition);
    const fromCenterToMarker = markerPointWorld
      .clone()
      .sub(ball.position)
      .normalize();
    const fromCenterToCamera = cameraTargetPosition
      .clone()
      .sub(ball.position)
      .normalize();

    // Paso 2: alinear marker → cámara (ya funciona bien en tu código)
    const rotationQuat = new THREE.Quaternion().setFromUnitVectors(
      fromCenterToMarker,
      fromCenterToCamera,
    );
    ball.quaternion.premultiply(rotationQuat);

    // Paso 3: aplicar la misma rotación a thumb para saber dónde quedó
    const camForward = fromCenterToCamera.clone();
    let projectedUp = null;
    if (gripLineDir && gripLineDir.lengthSq() > 1e-8) {
      const rotatedGripLine = gripLineDir
        .clone()
        .applyQuaternion(ball.quaternion);
      projectedUp = rotatedGripLine
        .clone()
        .projectOnPlane(camForward)
        .normalize();
    } else if (thumbPointLocal) {
      const rotatedThumb = thumbPointLocal
        .clone()
        .applyQuaternion(ball.quaternion)
        .add(ball.position);
      // Paso 4: proyectar vector de 'up' desde marker hacia thumb en plano perpendicular a la cámara
      projectedUp = rotatedThumb
        .clone()
        .sub(markerPointWorld)
        .projectOnPlane(camForward)
        .normalize();
    }

    // Paso 5: calcular ángulo entre el vector `projectedUp` y el vector que debería ser "up" (eje Y pantalla)
    if (projectedUp && projectedUp.lengthSq() > 1e-8) {
      const screenDown = new THREE.Vector3(0, -1, 0); // dirección vertical en pantalla
      const angle = projectedUp.angleTo(screenDown);
      const cross = new THREE.Vector3().crossVectors(projectedUp, screenDown);
      const sign = Math.sign(cross.dot(camForward)); // determina sentido

      // Paso 6: rotar alrededor del eje de cámara para corregir el roll
      const rollQuat = new THREE.Quaternion().setFromAxisAngle(
        camForward,
        angle * sign,
      );
      ball.quaternion.premultiply(rollQuat);
    }
  }

  return (
    <group ref={ref}>
      <primitive object={ball} />
    </group>
  );
});

BowlingLayoutDualAngle.displayName = "BowlingLayoutDualAngle";
